import { motion } from "framer-motion";
import styled, { css } from "styled-components/macro";
import {
  borderRadius,
  breakpoint,
  colors,
  fontStyle,
  fontSize,
  space,
} from "shared/styles/constants";
import { ITheme } from "shared/types";

export interface StyledTableProps {
  theme?: ITheme;
  stripedRow?: boolean;
  isOutline?: boolean;
  verticalAlignCells?: "top" | "middle";
  width?: "100%" | "max-content";
}

export const TableOverflowWrapper = styled.div`
  max-width: 100%;
  overflow: hidden;
`;

export const TableScrollWrapper = styled.div`
  overflow-x: auto;
  max-width: 100vw;
`;

export const StyledTable = styled.table<StyledTableProps>`
  border: none;
  border-collapse: collapse;

  width: ${({ width }) => width || "100%"};
  max-width: 100%;
`;

export const StyledThead = styled.thead`
  @media print {
    display: table-row-group; // Don't show table headers on each page.
  }
`;

export const StyledTooltip = styled.span`
  position: relative;
  top: -2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 5px;
  vertical-align: middle;
`;

interface StyledCellProps extends StyledTableProps {
  hideOnMobile?: boolean;
  isActionColumn?: boolean;
}

const getStripeColor = (
  isOutline?: boolean,
  stripedRow?: boolean,
  theme?: ITheme
) => {
  if (isOutline) {
    return colors.black;
  }

  switch (theme) {
    case ITheme.secondary:
      return stripedRow ? colors.grey2 : colors.grey1;
    case ITheme.tertiary:
      return stripedRow ? colors.blackLight : colors.extraBlack;
    default:
      return stripedRow ? colors.grey1 : colors.blackLight;
  }
};

export const StyledCell = styled.td<StyledCellProps>`
  text-align: ${({ isOutline }) => (isOutline ? "center" : "left")};

  padding: ${space.s};
  vertical-align: ${({ verticalAlignCells }) => verticalAlignCells || "middle"};

  background-color: ${({ isOutline, stripedRow, theme }) =>
    getStripeColor(isOutline, stripedRow, theme)};

  ${breakpoint("s", "down")} {
    ${({ hideOnMobile }) => hideOnMobile && "display: none"};
  }

  ${({ isActionColumn }) =>
    isActionColumn &&
    css`
      // hack to fix width to the width of content
      width: 0.1%;
      white-space: nowrap;
    `}
`;

interface StyledHeaderRowProps extends StyledTableProps {
  isMultiHeading?: boolean;
}

export const StyledHeaderRow = styled.tr<StyledHeaderRowProps>`
  text-align: ${({ isOutline }) => (isOutline ? `center` : `left`)};
  text-transform: uppercase;
  letter-spacing: 1px;
  ${fontStyle.bold}
  ${fontSize.xxs}

  ${({ isMultiHeading }) =>
    isMultiHeading &&
    css`
      &:nth-child(1) td {
        background-color: ${colors.grey3};

        &:first-child {
          border-radius: ${borderRadius.m} 0 0 0;
        }

        &:last-child {
          border-radius: 0 ${borderRadius.m} 0 0;
        }
      }

      &:nth-child(2) td {
        background-color: ${colors.grey2};

        + td {
          border-left: 1px solid ${colors.grey1};
        }
      }

      &:nth-child(3) td {
        background-color: ${colors.grey1};

        + td {
          border-left: 1px solid ${colors.grey2};
        }
      }
    `};
  @media print {
    background-color: transparent;
    page-break-inside: avoid;

    * {
      background-color: transparent;
    }
  }
`;

interface StyledRowProps extends StyledTableProps {
  expanded?: boolean;
}

export const StyledRow = styled.tr<StyledRowProps>`
  td:first-child {
    padding-left: ${space.s};
    border-radius: ${({ expanded }) =>
      expanded
        ? `${borderRadius.m} 0 0 0`
        : `${borderRadius.m} 0 0 ${borderRadius.m}`};
  }

  td:last-child {
    padding-right: ${space.s};
    border-radius: ${({ expanded }) =>
      expanded
        ? `0 ${borderRadius.m} 0 0`
        : `0 ${borderRadius.m} ${borderRadius.m} 0`};
  }

  ${({ isOutline }) =>
    isOutline &&
    css`
      td {
        border: 1px solid ${colors.grey1};
      }
    `}

  @media print {
    page-break-inside: avoid;
    * {
      border-radius: 0 !important;
      background-color: transparent;
    }
    :nth-of-type(even) {
      background-color: ${colors.grey6} !important;
    }
  }
`;

export const StyledOpenRow = styled(motion.tr)<StyledTableProps>`
  ${({ isOutline, stripedRow, theme }) =>
    stripedRow &&
    css`
      background-color: ${getStripeColor(isOutline, stripedRow, theme)};
    `};

  border-radius: 0 0 ${borderRadius.m} ${borderRadius.m};
`;

export const OpenRowCell = styled.td`
  padding: ${space.m};
`;

export const OpenRowContent = styled.div`
  position: relative;

  // prevent overflow on mobile
  max-width: calc(100vw - ${space.xxxl} - ${space.m});
`;

export const EmptyTableContent = styled.div`
  width: fit-content;
  margin: ${space.xs} auto;
`;
